import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { QueryProvider } from '@/providers/QueryProvider'
import { GoogleReCaptchaClientProvider } from '@/providers/GoogleReCaptchaProvider'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { AppLayout } from '@/components/layout/AppLayout'
import { Skeleton } from '@/components/ui/skeleton'
import { Login } from '@/pages/Login'
import { LoginOtp } from '@/pages/LoginOtp'
import { Toaster } from 'sonner'

function PageSkeleton() {
  return (
    <div className="space-y-6 p-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
      <Skeleton className="h-48 w-full rounded-xl" />
      <div className="grid grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    </div>
  )
}

const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const Network = lazy(() => import('@/pages/Network').then((m) => ({ default: m.Network })))
const Stats = lazy(() => import('@/pages/Stats').then((m) => ({ default: m.Stats })))
const Share = lazy(() => import('@/pages/Share').then((m) => ({ default: m.Share })))

function RootRedirect() {
  const { user } = useAuth()
  return user ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
}

function App() {
  return (
    <ErrorBoundary>
      <GoogleReCaptchaClientProvider>
        <Router>
          <QueryProvider>
            <AuthProvider>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/login/otp" element={<LoginOtp />} />

                <Route element={<ProtectedRoute />}>
                  <Route element={<AppLayout />}>
                    <Route
                      path="/dashboard"
                      element={
                        <Suspense fallback={<PageSkeleton />}>
                          <Dashboard />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/network"
                      element={
                        <Suspense fallback={<PageSkeleton />}>
                          <Network />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/stats"
                      element={
                        <Suspense fallback={<PageSkeleton />}>
                          <Stats />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/share"
                      element={
                        <Suspense fallback={<PageSkeleton />}>
                          <Share />
                        </Suspense>
                      }
                    />
                  </Route>
                </Route>

                <Route path="/" element={<RootRedirect />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>

              <Toaster position="top-center" richColors />
            </AuthProvider>
          </QueryProvider>
        </Router>
      </GoogleReCaptchaClientProvider>
    </ErrorBoundary>
  )
}

export default App
